import type {
  AbilityGrade,
  AbilityGrades,
  DraftPick,
  DraftProspect,
  GameState,
  Player,
  Position,
  RecruitmentState,
  ScoutingLevel,
  SeasonState,
  TeamPlayer,
  TradeStatus,
} from './types'
import { buildLadder, createSeasonState, prepareSeasonRound } from './season'
import { createPlayerDevelopmentState } from './progression'
import { USER_TEAM_ID } from './seasonData'
import { recordAcceptedTrade, recordSeasonTransition, syncCareerHistory } from './history'
import { refreshClubStrategyForSeason } from './clubStrategy'
import {
  hydrateContracts,
  applyAnnualEscalation,
  decrementContractYears,
  delistPlayerContract,
  recalculateSalaryCap,
} from './contracts'
import { rollStaffMarketSeason } from './staffMarket'
import { processSeasonEndRevenue } from './sponsorship'

const FIRST_NAMES = ['Alby', 'Bailey', 'Caleb', 'Darcy', 'Eli', 'Finn', 'Hugo', 'Isaac', 'Jai', 'Kade', 'Lachlan', 'Mason', 'Noah', 'Oscar', 'Paddy', 'Quinn', 'Riley', 'Sam', 'Toby', 'Xavier']
const LAST_NAMES = ['Archer', 'Banks', 'Corbett', 'Dwyer', 'Ellis', 'Frost', 'Gannon', 'Hale', 'Irwin', 'Judd', 'Keating', 'Laird', 'Morrow', 'Nash', 'Olsen', 'Pike', 'Quade', 'Rowe', 'Sutton', 'Voss']
const STATES = ['WA', 'Victoria', 'South Australia', 'Tasmania', 'NSW', 'Queensland', 'NT']
const POSITIONS: Position[] = ['FB', 'HB', 'C', 'HF', 'F', 'FOL']
const ROLES: Record<Position, string[]> = {
  FB: ['Lockdown defender', 'Intercept defender'], HB: ['Rebounding defender', 'Defensive distributor'],
  C: ['Outside runner', 'Two-way wing'], HF: ['High half-forward', 'Forward connector'],
  F: ['Pressure forward', 'Key forward'], FOL: ['Inside midfielder', 'Developing ruck'], INT: ['Utility'], EMG: ['Utility'],
}
const ARCHETYPES = ['Ready-made contributor', 'High-upside athlete', 'Skilled ball user', 'Competitive organiser', 'Raw long-term project', 'Versatile system player']
const NATIONAL_POOL_SIZE = 76 // four selections for each of 19 clubs
const ABILITY_KEYS: (keyof AbilityGrades)[] = [
  'kicking', 'handball', 'marking', 'groundBall', 'tackling', 'ruckWork', 'goalSense', 'defensiveCraft',
  'contestedAbility', 'speed', 'acceleration', 'endurance', 'strength', 'agility', 'leap', 'recovery',
  'durability', 'positioning', 'decisionMaking', 'anticipation', 'spatialAwareness', 'tacticalDiscipline',
  'adaptability', 'composure', 'professionalism', 'coachability', 'competitiveness', 'resilience', 'loyalty',
  'ambition', 'selflessness', 'leadership', 'consistency', 'bigGameTemperament',
]

const hash = (value: string) => {
  let h = 2166136261
  for (let i = 0; i < value.length; i += 1) h = Math.imul(h ^ value.charCodeAt(i), 16777619)
  return h >>> 0
}
const value = (seed: number, key: string, min: number, max: number) => min + (hash(`${seed}:${key}`) % (max - min + 1))
const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n))
const prospectScore = (p: DraftProspect) => p.hidden.football * .34 + p.hidden.athleticism * .2 + p.hidden.character * .13 + p.hidden.potential * .27 + p.hidden.durability * .06

export const createRecruitmentState = (seed: number, season: number, players: Player[]): RecruitmentState => {
  const raw = Array.from({ length: NATIONAL_POOL_SIZE }, (_, index): DraftProspect => {
    const first = FIRST_NAMES[(index + value(seed, 'first-offset', 0, 19)) % FIRST_NAMES.length]
    const last = LAST_NAMES[(index * 3 + Math.floor(index / FIRST_NAMES.length) * 7 + value(seed, 'last-offset', 0, 19)) % LAST_NAMES.length]
    const position = POSITIONS[index % POSITIONS.length]
    const football = value(seed, `football-${index}`, 48, 88)
    const athleticism = value(seed, `athletic-${index}`, 45, 92)
    const character = value(seed, `character-${index}`, 42, 95)
    const potential = value(seed, `potential-${index}`, 50, 96)
    const durability = value(seed, `durability-${index}`, 46, 94)
    return {
      id: `s${season}-prospect-${String(index + 1).padStart(2, '0')}`,
      name: `${first} ${last}`,
      age: 18 + value(seed, `age-${index}`, 0, 1),
      position,
      role: ROLES[position][value(seed, `role-${index}`, 0, ROLES[position].length - 1)],
      archetype: ARCHETYPES[value(seed, `arch-${index}`, 0, ARCHETYPES.length - 1)],
      state: STATES[value(seed, `state-${index}`, 0, STATES.length - 1)],
      projectedMin: 1,
      projectedMax: 60,
      summary: `${position} prospect with ${football >= 72 ? 'advanced football craft' : athleticism >= 75 ? 'notable athletic tools' : 'a developing all-round profile'}.`,
      risk: durability < 58 || character < 55 ? 'High' : durability < 70 || character < 67 ? 'Medium' : 'Low',
      hidden: { football, athleticism, character, potential, durability },
    }
  })
  const ranking = [...raw].sort((a, b) => prospectScore(b) - prospectScore(a) || a.id.localeCompare(b.id))
  const rankById = new Map(ranking.map((prospect, index) => [prospect.id, index + 1]))
  const prospects = raw.map(prospect => {
    const rank = rankById.get(prospect.id) ?? NATIONAL_POOL_SIZE
    return { ...prospect, projectedMin: clamp(rank - 5, 1, NATIONAL_POOL_SIZE), projectedMax: clamp(rank + 9, 1, NATIONAL_POOL_SIZE) }
  })
  return {
    season, seed, prospects, watchlist: [], scouting: {},
    tradeStatuses: Object.fromEntries(players.map(player => [player.id, 'required' as TradeStatus])),
    scoutingActions: 3, actionsRound: 1,
    draft: { status: 'not-started', order: [], currentPick: 0 },
    tradeOffers: [], acquiredPicks: [], delistedPlayerIds: [],
  }
}

export const hydrateRecruitmentState = (saved: RecruitmentState | undefined, seed: number, season: number, players: Player[]): RecruitmentState => {
  const canonical = createRecruitmentState(seed, season, players)
  if (!saved || !Array.isArray(saved.prospects) || saved.prospects.length !== NATIONAL_POOL_SIZE) return canonical
  const prospectIds = new Set(canonical.prospects.map(p => p.id))
  const validStatuses = new Set<TradeStatus>(['required', 'right-offer', 'open', 'delist'])
  return {
    ...canonical,
    ...saved,
    prospects: saved.prospects,
    watchlist: (saved.watchlist ?? []).filter(id => prospectIds.has(id)),
    scouting: Object.fromEntries(Object.entries(saved.scouting ?? {}).filter(([id]) => prospectIds.has(id))),
    tradeStatuses: Object.fromEntries(players.map(player => [player.id, validStatuses.has(saved.tradeStatuses?.[player.id]) ? saved.tradeStatuses[player.id] : 'required'])),
    draft: saved.draft ?? canonical.draft,
    tradeOffers: saved.tradeOffers ?? [],
    acquiredPicks: saved.acquiredPicks ?? [],
    delistedPlayerIds: saved.delistedPlayerIds ?? [],
  }
}

export const togglePlayerDelisting = (recruitment: RecruitmentState, playerId: string): RecruitmentState => ({
  ...recruitment,
  delistedPlayerIds: recruitment.delistedPlayerIds.includes(playerId)
    ? recruitment.delistedPlayerIds.filter(id => id !== playerId)
    : [...recruitment.delistedPlayerIds, playerId],
})

export const toggleProspectWatchlist = (recruitment: RecruitmentState, prospectId: string): RecruitmentState => ({
  ...recruitment,
  watchlist: recruitment.watchlist.includes(prospectId)
    ? recruitment.watchlist.filter(id => id !== prospectId)
    : [...recruitment.watchlist, prospectId],
})

export const scoutProspect = (recruitment: RecruitmentState, prospectId: string, round: number): RecruitmentState => {
  const freshActions = recruitment.actionsRound === round ? recruitment.scoutingActions : 3
  const current = recruitment.scouting[prospectId] ?? { level: 0 as ScoutingLevel, lastScoutedRound: 0 }
  if (freshActions <= 0 || current.level >= 3 || current.lastScoutedRound === round) return { ...recruitment, scoutingActions: freshActions, actionsRound: round }
  return {
    ...recruitment,
    scoutingActions: freshActions - 1,
    actionsRound: round,
    scouting: { ...recruitment.scouting, [prospectId]: { level: (current.level + 1) as ScoutingLevel, lastScoutedRound: round } },
  }
}

export const setPlayerTradeStatus = (recruitment: RecruitmentState, playerId: string, status: TradeStatus): RecruitmentState => ({
  ...recruitment,
  tradeStatuses: { ...recruitment.tradeStatuses, [playerId]: status },
})

export const visibleProspectAssessment = (prospect: DraftProspect, level: ScoutingLevel) => {
  const uncertainty = [18, 12, 7, 3][level]
  const band = (score: number) => `${clamp(score - uncertainty, 1, 99)}–${clamp(score + uncertainty, 1, 99)}`
  return {
    football: band(prospect.hidden.football), athleticism: band(prospect.hidden.athleticism),
    character: level >= 2 ? band(prospect.hidden.character) : 'Unknown',
    potential: level >= 1 ? band(prospect.hidden.potential) : 'Unknown',
    durability: level >= 3 ? band(prospect.hidden.durability) : 'Unknown',
    confidence: ['Initial view', 'Observed', 'Detailed', 'Complete file'][level],
  }
}

const draftOrder = (season: SeasonState): DraftPick[] => {
  const reverse = [...buildLadder(season, season.completedThroughRound)].reverse().map(row => row.teamId)
  return Array.from({ length: 4 }, (_, round) => reverse.map((clubId, index) => ({
    overall: round * reverse.length + index + 1, round: round + 1, clubId, prospectId: null,
  }))).flat()
}

export const openTradePeriod = (state: GameState): GameState => {
  if (!state.season || state.recruitment.tradeOffers.length || state.recruitment.draft.status !== 'not-started') return state
  const order = draftOrder(state.season)
  const clubs = Object.keys(state.season.teams).filter(clubId => clubId !== USER_TEAM_ID)
  const usedPicks = new Set<number>()
  const tradeOffers = state.club.players.flatMap(player => {
    const status = state.recruitment.tradeStatuses[player.id] ?? 'required'
    if (status === 'required' || player.id.startsWith('draftee-')) return []
    const clubId = clubs[hash(`${state.recruitment.seed}:${player.id}:club`) % clubs.length]
    const preferredRound = status === 'delist' ? 4 : status === 'open' && (player.age <= 24 || player.form >= 70) ? 2 : 3
    const pick = order.find(item => item.clubId === clubId && item.round >= preferredRound && !usedPicks.has(item.overall))
      ?? order.find(item => item.clubId === clubId && !usedPicks.has(item.overall))
    if (!pick) return []
    usedPicks.add(pick.overall)
    return [{ id: `offer-${player.id}-${clubId}`, playerId: player.id, clubId, pickOverall: pick.overall, status: 'pending' as const }]
  })
  return { ...state, recruitment: { ...state.recruitment, tradeOffers } }
}

export const respondToTradeOffer = (state: GameState, offerId: string, accept: boolean): GameState => {
  if (state.recruitment.draft.status !== 'not-started') return state
  const offer = state.recruitment.tradeOffers.find(item => item.id === offerId)
  if (!offer || offer.status !== 'pending') return state
  const tradeOffers = state.recruitment.tradeOffers.map(item => item.id === offerId ? { ...item, status: accept ? 'accepted' as const : 'rejected' as const } : item)
  if (!accept) return { ...state, recruitment: { ...state.recruitment, tradeOffers } }
  const history = recordAcceptedTrade(state, offer)
  const team = Object.fromEntries(Object.entries(state.staffPlan.team).map(([group, players]) => [group, players.filter((entry: { playerId: string }) => entry.playerId !== offer.playerId)])) as unknown as GameState['staffPlan']['team']
  return {
    ...state,
    history,
    club: { ...state.club, players: state.club.players.filter(player => player.id !== offer.playerId) },
    staffPlan: { ...state.staffPlan, team },
    recruitment: {
      ...state.recruitment,
      tradeOffers,
      acquiredPicks: [...new Set([...state.recruitment.acquiredPicks, offer.pickOverall])],
    },
  }
}

const autoAdvance = (recruitment: RecruitmentState): RecruitmentState => {
  const draft = { ...recruitment.draft, order: recruitment.draft.order.map(pick => ({ ...pick })) }
  const selected = new Set(draft.order.flatMap(pick => pick.prospectId ? [pick.prospectId] : []))
  while (draft.currentPick < draft.order.length && draft.order[draft.currentPick].clubId !== USER_TEAM_ID) {
    const pick = draft.order[draft.currentPick]
    const best = recruitment.prospects
      .filter(prospect => !selected.has(prospect.id))
      .sort((a, b) => {
        const aScore = prospectScore(a) + (hash(`${pick.clubId}:${a.position}:${pick.overall}`) % 11)
        const bScore = prospectScore(b) + (hash(`${pick.clubId}:${b.position}:${pick.overall}`) % 11)
        return bScore - aScore || a.id.localeCompare(b.id)
      })[0]
    if (!best) break
    pick.prospectId = best.id
    selected.add(best.id)
    draft.currentPick += 1
  }
  if (draft.currentPick >= draft.order.length) draft.status = 'complete'
  return { ...recruitment, draft }
}

export const previewDraftOrder = (recruitment: RecruitmentState, season: SeasonState): DraftPick[] =>
  draftOrder(season).map(pick => recruitment.acquiredPicks.includes(pick.overall)
    ? { ...pick, clubId: USER_TEAM_ID }
    : pick)

export const beginDraft = (recruitment: RecruitmentState, season: SeasonState): RecruitmentState => {
  if (recruitment.tradeOffers.some(offer => offer.status === 'pending')) return recruitment
  if (recruitment.draft.status !== 'not-started') return autoAdvance(recruitment)
  return autoAdvance({ ...recruitment, draft: { status: 'active', order: previewDraftOrder(recruitment, season), currentPick: 0 } })
}

const grade = (score: number): AbilityGrade => score >= 90 ? 'Elite' : score >= 80 ? 'Strong' : score >= 70 ? 'Above Standard' : score >= 58 ? 'League Standard' : score >= 47 ? 'Below Standard' : 'Limited'
const prospectToPlayer = (prospect: DraftProspect, pick: number, season: number): Player => {
  const base = prospect.hidden.football * .55 + prospect.hidden.athleticism * .25 + prospect.hidden.potential * .2
  const abilities = Object.fromEntries(ABILITY_KEYS.map(key => [
    key,
    grade(clamp(Math.round(base + (hash(`${prospect.id}:${key}`) % 17) - 8), 35, 92)),
  ])) as unknown as AbilityGrades
  const player: Player = {
    id: `draftee-${prospect.id}`, name: prospect.name, age: prospect.age, primaryRole: prospect.role, position: prospect.position,
    abilities, form: 50, fitness: 94, fatigue: 4, morale: 72, contractYears: 2, developmentFocus: null,
    isSelected: false, positionInTeam: null, roleInTeam: null,
    visual: { description: `${prospect.name}, ${prospect.archetype.toLowerCase()}`, traits: [prospect.archetype, prospect.state], faceSeed: prospect.id },
    draftedSeason: season,
    draftedByClubId: USER_TEAM_ID,
    clubHistory: [{ clubId: USER_TEAM_ID, fromSeason: season + 1, arrival: 'draft' }],
    development: undefined as unknown as Player['development'],
    staffConcern: pick > 18 ? 'Later selection — development timeline remains uncertain.' : undefined,
  }
  player.development = createPlayerDevelopmentState(player)
  return player
}

export const selectDraftProspect = (state: GameState, prospectId: string): GameState => {
  if (!state.season || state.recruitment.draft.status !== 'active') return state
  const recruitment = state.recruitment
  const draft = { ...recruitment.draft, order: recruitment.draft.order.map(pick => ({ ...pick })) }
  const current = draft.order[draft.currentPick]
  if (!current || current.clubId !== USER_TEAM_ID || current.prospectId || draft.order.some(pick => pick.prospectId === prospectId)) return state
  const prospect = recruitment.prospects.find(item => item.id === prospectId)
  if (!prospect) return state
  current.prospectId = prospectId
  draft.currentPick += 1
  const advanced = autoAdvance({ ...recruitment, draft })
  const playerId = `draftee-${prospect.id}`
  const players = state.club.players.some(player => player.id === playerId)
    ? state.club.players
    : [...state.club.players, prospectToPlayer(prospect, current.overall, recruitment.season)]
  return syncCareerHistory({ ...state, recruitment: advanced, club: { ...state.club, players } })
}

export const startNextSeasonPreseason = (current: GameState): GameState => {
  if (current.preseasonComplete === false || current.recruitment.draft.status !== 'complete') return current
  const requiredDelistings = Math.max(0, current.club.players.length - 24)
  const selected = current.recruitment.delistedPlayerIds.filter(id => current.club.players.some(player => player.id === id && !player.id.startsWith('draftee-')))
  if (selected.length !== requiredDelistings) return current
  const nextSeason = (current.season?.season ?? current.recruitment.season) + 1
  const history = recordSeasonTransition(current, selected, nextSeason)
  const delisted = new Set(selected)
  let players = current.club.players.filter(player => !delisted.has(player.id)).map(player => ({ ...player, age: player.age + 1, form: 50, fitness: 94, fatigue: 0 }))

  // Process delisted player contracts (become dead cap)
  let club = current.club
  for (const playerId of selected) {
    club = delistPlayerContract({ ...current, club }, playerId).club
  }

  // Apply annual escalation to remaining contracts
  club = applyAnnualEscalation({ ...current, club }).club

  // Decrement contract years (expire if 0)
  club = decrementContractYears({ ...current, club }).club

  // Hydrate contracts for new draftees
  const tempState = { ...current, club: { ...club, players } }
  club = { ...club, contracts: hydrateContracts(tempState, club.contracts) }

  // Recalculate salary cap
  club = { ...club, salaryCap: recalculateSalaryCap({ ...current, club }) }

  const team = Object.fromEntries(Object.entries(current.staffPlan.team).map(([group, entries]) => [group, entries.filter((entry: TeamPlayer) => !delisted.has(entry.playerId))])) as GameState['staffPlan']['team']
  const preseason = prepareSeasonRound({
    ...current,
    history,
    round: 1,
    phase: 'preseason',
    preseasonComplete: false,
    preseasonCoachPlanConfirmed: false,
    club: { ...club, record: { wins: 0, losses: 0, draws: 0 }, players },
    staffPlan: { ...current.staffPlan, team },
    season: createSeasonState(false, nextSeason, current.season?.careerSeed ?? `legacy-${current.seed}`),
    week: undefined,
    coachDevelopment: { ...current.coachDevelopment, season: nextSeason, isLocked: false },
    matchResult: null,
    consequenceReport: null,
  }, 1)
  const refreshed = refreshClubStrategyForSeason({ ...preseason, phase: 'preseason', preseasonComplete: false, preseasonCoachPlanConfirmed: false })
  // Process season-end revenue (broadcast, sponsorship performance, sponsor contract aging)
  const withRevenue = processSeasonEndRevenue(refreshed)
  return rollStaffMarketSeason(withRevenue, nextSeason)
}
